/**
 * manages the different display effects for the carousel
 */
export default class EffectManager {
	/** @type {Object} shared module context */
	#ctx;

	/** @type {Object} registry of available effects */
	#effectRegistry;

	/** @type {Object|null} current effect instance */
	#currentEffect = null;

	/**
	 * @constructor
	 * @param {Object} ctx - shared module context containing emitter, events, store, etc.
	 * @param {Object} effectRegistry - registry of available effect classes
	 */
	constructor(ctx, effectRegistry) {
		const _ = this;
		_.#ctx = ctx;
		_.#effectRegistry = effectRegistry;

		// define all event handlers in one object
		_.handlers = {
			// handler for store's optionsChanged event
			optionsChanged: ({ currentOptions }) => {
				const currentEffectName =
					_.#currentEffect?.constructor?.effectName || _.#currentEffect?.constructor?.name;

				// only load new effect if the name has changed
				if (currentOptions.effect !== currentEffectName) {
					_.loadEffect(currentOptions.effect);
				}
			},
		};

		_.init();
	}

	init() {
		const _ = this;
		_.bindEvents();

		// listen for late effect registrations so we can swap automatically
		_.handlers.effectRegistered = (e) => {
			try {
				const registered = e?.detail?.effectName;
				const desired = String(_.#ctx.store.getOptions().effect || '').toLowerCase();
				if (registered && desired && registered === desired) {
					_.loadEffect(desired);
				}
			} catch (err) {
				// ignore
			}
		};
		if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
			window.addEventListener('tarot:effect-registered', _.handlers.effectRegistered);
		}
	}

	// reinitialize on options changes
	reInit() {
		this.loadCurrentEffect();
	}

	// bind store events to handlers
	bindEvents() {
		this.#ctx.emitter.on(this.#ctx.events.store.optionsChanged, this.handlers.optionsChanged);
	}

	// load the current effect based on current options
	loadCurrentEffect() {
		this.loadEffect(this.#ctx.store.getOptions().effect);
	}

	/**
	 * load a specific effect by name
	 * @param {string} effectName - name of the effect to load
	 */
	loadEffect(effectName) {
		const _ = this;
		if (!effectName) {
			console.error('Error: no effect detected or could not load effect ', effectName);
			return;
		}

		const NewEffectClass = _.#effectRegistry[effectName];

		if (!NewEffectClass) {
			// Effect not loaded yet - fall back to carousel and auto-upgrade when it registers
			if (effectName !== 'carousel') {
				this.loadEffect('carousel');
			}
			return;
		}

		// store reference to previous effect for event
		const previousEffect = _.#currentEffect;
		const previousEffectName =
			previousEffect?.constructor?.effectName || previousEffect?.constructor?.name;

		// clear out old effect
		if (_.#currentEffect) {
			_.#currentEffect.destroy();

			// emit destroyed event
			_.#ctx.emitter.emit(_.#ctx.events.effect.destroyed, {
				effectName: previousEffectName,
			});
		}

		// create new effect instance (pass ctx instead of carousel)
		_.#currentEffect = new NewEffectClass(_.#ctx);

		// set name in DOM
		_.#ctx.carousel.setAttribute('effect', effectName);

		// emit loaded event
		_.#ctx.emitter.emit(_.#ctx.events.effect.loaded, {
			effect: _.#currentEffect,
			effectName,
		});

		// emit changed event
		_.#ctx.emitter.emit(_.#ctx.events.effect.changed, {
			previousEffect,
			currentEffect: _.#currentEffect,
			effectName,
		});
	}

	/**
	 * get the current effect instance
	 * @returns {Object|null} current effect instance
	 */
	getEffect() {
		return this.#currentEffect;
	}

	/**
	 * destroy the effect manager, unbinding all events
	 */
	destroy() {
		const _ = this;

		// unbind from global + store events
		if (
			typeof window !== 'undefined' &&
			typeof window.removeEventListener === 'function' &&
			_.handlers?.effectRegistered
		) {
			window.removeEventListener('tarot:effect-registered', _.handlers.effectRegistered);
		}
		_.#ctx.emitter.off(_.#ctx.events.store.optionsChanged, _.handlers.optionsChanged);

		// destroy current effect if exists
		if (_.#currentEffect) {
			const effectName =
				_.#currentEffect.constructor?.effectName || _.#currentEffect.constructor?.name || null;
			_.#currentEffect.destroy();

			// emit destroyed event
			_.#ctx.emitter.emit(_.#ctx.events.effect.destroyed, {
				effectName,
			});

			_.#currentEffect = null;
		}
	}
}
